import {loader, autoDetectRenderer} from 'pixi.js';
import {noop as _noop} from 'lodash/util';
import {delay as _delay} from 'lodash/function';
import levels from '../data/levels.json';
import Stage from './Stage';
import sound from'./Sound';
import { isNullOrUndefined } from 'util';
import { isNull } from 'util';
import Highscore from './Highscore';

const BLUE_SKY_COLOR = 0x64b0ff;
const PINK_SKY_COLOR = 0xECB7C5;
const SUCCESS_RATIO = 0.6;

class Game {
  /**
   * Game Constructor
   * @param opts
   * @param {String} opts.spritesheet Path to the spritesheet file that PIXI's loader should load
   * @returns {Game}
   */
  constructor(opts) {
    this.spritesheet = opts.spritesheet;
    this.loader = loader;
    this.renderer =  autoDetectRenderer(window.innerWidth, window.innerHeight, {
      backgroundColor: BLUE_SKY_COLOR
    });
    this.levelIndex = 0;

    this.waveEnding = false;
    this.quackingSoundId = null;
    this.musicId = null;
    this.mute = false;
    this.levels = levels.normal;
    return this;
  }

  get tramsMissed() {
    return this.tramsMissedVal ? this.tramsMissedVal : 0;
  }

  set tramsMissed(val) {
    this.tramsMissedVal = val;

    if (this.stage && this.stage.hud) {

      if (!this.stage.hud.hasOwnProperty('tramsMissed')) {
        this.stage.hud.createTextureBasedCounter('tramsMissed', {
          texture: 'hud/score-sad/0.png',
          spritesheet: this.spritesheet,
          location: Stage.sadStatusBoxLocation()
        });
      }

      this.stage.hud.tramsMissed = val;
    }
  }

  get tramsShot() {
    return this.tramsShotVal ? this.tramsShotVal : 0;
  }

  set tramsShot(val) {
    this.tramsShotVal = val;

    if (this.stage && this.stage.hud) {

      if (!this.stage.hud.hasOwnProperty('tramsShot')) {
        this.stage.hud.createTextureBasedCounter('tramsShot', {
          texture: 'hud/score-smile/0.png',
          spritesheet: this.spritesheet,
          location: Stage.smileStatusBoxLocation()
        });
      }

      this.stage.hud.tramsShot = val;
    }
  }
  /**
   * flags - getter
   * @returns {Number}
   */
  get flags() {
    return this.flagsVal ? this.flagsVal : 0;
  }

  /**
   * flags - setter
   * Setter for the bullets property of the game. Also in charge of updating the HUD. In the event
   * the HUD doesn't know about displaying bullets, the property and a corresponding texture container
   * will be created in HUD.
   * @param {Number} val Number of bullets
   */
  set flags(val) {
    this.flagsVal = val;

    if (this.stage && this.stage.hud) {

      if (!this.stage.hud.hasOwnProperty('flags')) {
        this.stage.hud.createTextureBasedCounter('flags', {
          texture: 'hud/flag/0.png',
          spritesheet: this.spritesheet,
          location: Stage.flagStatusBoxLocation()
        });
      }

      this.stage.hud.flags = val;
    }

  }

  /**
   * score - getter
   * @returns {Number}
   */
  get score() {
    return this.scoreVal ? this.scoreVal : 0;
  }

  /**
   * score - setter
   * Setter for the score property of the game. Also in charge of updating the HUD. In the event
   * the HUD doesn't know about displaying the score, the property and a corresponding text box
   * will be created in HUD.
   * @param {Number} val Score value to set
   */
  set score(val) {
    this.scoreVal = val;

    if (this.stage && this.stage.hud) {

      if (!this.stage.hud.hasOwnProperty('score')) {
        this.stage.hud.createTextBox('score', {
          style: {
            fontFamily: 'Arial',
            fontSize: '25px',
            align: 'center',
            fill: 'white'
          },
          location: Stage.scoreBoxLocation(),
          anchor: {
            x: 1,
            y: 0
          }
        });
      }

      this.stage.hud.score = val;
    }

  }

  /**
   * wave - get
   * @returns {Number}
   */
  get wave() {
    return this.waveVal ? this.waveVal : 0;
  }

  /**
   * wave - set
   * Setter for the wave property of the game. Also in charge of updating the HUD. In the event
   * the HUD doesn't know about displaying the wave, the property and a corresponding text box
   * will be created in the HUD.
   * @param {Number} val
   */
  set wave(val) {
    this.waveVal = val;

    if (this.stage && this.stage.hud) {

      if (!this.stage.hud.hasOwnProperty('waveStatus')) {
        this.stage.hud.createTextBox('waveStatus', {
          style: {
            fontFamily: 'Arial',
            fontSize: '18px',
            align: 'left',
            fill: 'white'
          },
          location: Stage.waveStatusBoxLocation(),
          anchor: {
            x: 1,
            y: 1
          }
        });
      }

      if (!isNaN(val) && val > 0) {
        this.stage.hud.waveStatus = this.level.title + ', Fala ' + val + ' z ' + this.level.waves;
      } else {
        this.stage.hud.waveStatus = '';
      }
    }
  }

  /**
   * gameStatus - get
   * @returns {String}
   */
  get gameStatus() {
    return this.gameStatusVal ? this.gameStatusVal : '';
  }

  /**
   * gameStatus - set
   * @param {String} val
   */
  set gameStatus(val) {
    this.gameStatusVal = val;

    if (this.stage && this.stage.hud) {

      if (!this.stage.hud.hasOwnProperty('gameStatus')) {
        this.stage.hud.createTextBox('gameStatus', {
          style: {
            fontFamily: 'Arial',
            fontSize: '40px',
            align: 'left',
            fill: 'white'
          },
          location: Stage.gameStatusBoxLocation()
        });
      }

      this.stage.hud.gameStatus = val;
    }
  }

  load() {
    this.loader
      .add(this.spritesheet)
      .load(this.onLoad.bind(this));
  }

  onLoad() {
    document.body.appendChild(this.renderer.view);

    this.stage = new Stage({
      spritesheet: this.spritesheet
    });

    this.highscore = new Highscore();    
    this.highscore.visible = false;

    this.scaleToWindow();
    this.bindEvents();
    this.showMute();
    this.showPlay();
    this.showRanking();
    this.animate();

  }

  bindEvents() {
    window.addEventListener('resize', this.scaleToWindow.bind(this));
  }

  scaleToWindow() {
    this.renderer.resize(window.innerWidth, window.innerHeight);
    this.stage.scaleToWindow();
  }

  startLevel() {
    this.level = this.levels[this.levelIndex];
    this.tramsShot = 0;
    this.tramsMissed = 0;
    this.wave = 0;

    this.gameStatus = this.level.title;
    this.stage.preLevelAnimation().then(() => {
      this.gameStatus = '';
      this.bindInteractions();
      this.startWave();
    });
  }

  startWave() {
    //this.quackingSoundId = sound.play('quacking');
    this.wave += 1;
    this.waveStartTime = Date.now();
    this.flags = this.level.flags;
    this.tramsShotThisWave = 0;
    this.waveEnding = false;

    this.stage.addTrams(this.level.trams, this.level.speed);
    this.bindInteractions();
  }

  endWave() {
    this.waveEnding = true;
    this.flags = 0;
    //sound.stop(this.quackingSoundId);
    if (this.stage.tramsAlive()) {
      this.tramsMissed += this.level.trams - this.tramsShotThisWave;
      this.renderer.backgroundColor = PINK_SKY_COLOR;
      this.stage.flyAway().then(this.goToNextWave.bind(this));
    } else {
      this.stage.cleanUpTrams();
      this.goToNextWave();
    }
  }

  goToNextWave() {
    this.renderer.backgroundColor = BLUE_SKY_COLOR;
    if (this.level.waves === this.wave) {
      this.endLevel();
    } else {
      this.startWave();
    }
  }

  shouldWaveEnd() {
    // evaluate pre-requisites for a wave to end
    if (this.wave === 0 || this.waveEnding || this.stage.busActive()) {
      return false;
    }

    return this.isWaveTimeUp() || (this.outOfAmmo() && this.stage.tramsAlive()) || !this.stage.tramsActive();
  }

  isWaveTimeUp() {
    return this.level ? this.waveElapsedTime() >= this.level.time : false;
  }

  waveElapsedTime() {
    return (Date.now() - this.waveStartTime) / 1000;
  }

  outOfAmmo() {
    return this.level && this.flags === 0;
  }

  endLevel() {
    this.wave = 0;
    this.goToNextLevel();
  }

  goToNextLevel() {
    this.levelIndex++;
    if (!this.levelWon()) {
      this.loss();
    } else if (this.levelIndex < this.levels.length) {
      this.startLevel();
    } else {
      this.win();
    }
  }

  levelWon() {
    return this.tramsShot > SUCCESS_RATIO * this.level.trams * this.level.waves;
  }

  win() {
    if (!isNull(this.musicId)) 
    {
      sound.stop(this.musicId);
      this.musicId = null;
    }
    if (!this.mute) {
      sound.play('loserSound');
    }
    this.gameStatus = 'Tęcza uratowana!';
    this.showReplay(this.getScoreMessage());
  }

  loss() {
    if (!isNull(this.musicId)) 
    {
      sound.stop(this.musicId);
      this.musicId = null;
    }
    if (!this.mute) {
      sound.play('loserSound');
    }
    this.gameStatus = 'Koniec gry!';
    this.showReplay(this.getScoreMessage());
  }

  getScoreMessage() {
      let scoreMessage;

      if (this.score >= 9400) {
        scoreMessage = 'Nie da się lepiej!.';
      }

      if (this.score < 9400) {
        scoreMessage = 'Blisko perfekcji';
      }

      if (this.score <= 9000) {
        scoreMessage = 'Imponujący wynik';
      }

      if (this.score <= 8000) {
        scoreMessage = 'Pewne zwycięsto.'
      }

      if (this.score <= 6000) {
        scoreMessage = 'Mogło być lepiej.';
      }

      return scoreMessage;
  }

  showReplay(replayText) {
      this.stage.hud.createTextBox('replayButton', {
          location: Stage.replayButtonLocation()
      });
      this.stage.hud.replayButton = replayText + ' Jeszcze raz?';

      this.stage.hud.createTextBox('shareButton', {
        location: Stage.shareButtonLocation()
      });
      this.stage.hud.shareButton = 'Podziel się!';

      this.stage.hud.createTextBox('postButton', {
        location: Stage.postButtonLocation()
      });
      this.stage.hud.postButton = 'Dopisz do rankingu!';
      this.bindInteractions();

  }

  showMute() {
    this.stage.hud.createTextBox('muteButton', {
        location: Stage.muteButtonLocation(),
        anchor: {
          x: 0,
          y: 0
        }
    });
  this.stage.hud.muteButton = 'Wycisz';
  this.bindInteractions();

}

showPlay() {
  this.stage.hud.createTextBox('playButton', {
      location: Stage.playButtonLocation(),
      style: {
        fontFamily: 'Arial',
        fontSize: '40px',
        align: 'left',
        fill: 'white'
      }
  });
this.stage.hud.playButton = ' ';
this.bindInteractions();

}

showRanking() {
  this.stage.hud.createTextBox('rankingButton', {
      location: Stage.rankingButtonLocation(),
      style: {
        fontFamily: 'Arial',
        fontSize: '40px',
        align: 'left',
        fill: 'white'
      }
  });
this.stage.hud.rankingButton = ' ';
this.bindInteractions();

}

  handleClick(event) {
    let clickPoint = {
        x: event.data.global.x,
        y: event.data.global.y
    };

    if (this.stage.hud.muteButton && this.stage.clickedMute(clickPoint)) {
      this.handleMute();
      return;
    }

    if (!isNullOrUndefined(this.stage.hud.playButtonTextBox) || (this.stage.hud.playButton != " ")) {
      if (this.stage.hud.playButtonTextBox.visible)
      if (this.stage.clickedPlay(clickPoint))  {
        this.handlePlay();
        return;
      }
    }

    if (!isNullOrUndefined(this.stage.hud.rankingButtonTextBox) || (this.stage.hud.rankingButton != " ")) {
      if (this.stage.hud.rankingButtonTextBox.visible)
      if (this.stage.clickedRanking(clickPoint))  {
        this.handleRanking();
        return;
      }
    }

    if (this.highscore.visible === true) {
      this.removeRanking();
      return;
    }

    if (!this.stage.hud.replayButton && !this.outOfAmmo() && this.level) {
      //sound.play('gunSound');
      this.flags -= 1;
      this.updateScore(this.stage.shotsFired(clickPoint));
    }

    if (this.stage.hud.postButton && this.stage.clickedPost(clickPoint)) {
      this.handlePost();
    }

    if (this.stage.hud.shareButton && this.stage.clickedShare(clickPoint)) {
      this.handleShare();
    }

    if (this.stage.hud.replayButton && this.stage.clickedReplay(clickPoint)) {
      window.location.reload();
    }
  }

  handleMute() {
    if (!this.mute) {
      if (!isNullOrUndefined(this.musicId)) {
        sound.stop(this.musicId);
      }

      this.musicId = null;
      this.mute = true;
      this.stage.hud.muteButton = 'Włącz dźwięki';
    } else {
      this.musicId = sound.play('music');
      this.mute = false;
      this.stage.hud.muteButton = 'Wycisz';
    }
  }

  handlePlay() {

    
    this.stage.hud.playButton = " ";
    this.stage.hud.playButtonTextBox.visible = false;
    this.stage.hud.removeChild(this.stage.hud.playButtonTextBox);
    this.stage.hud.rankingButton = " ";
    this.stage.hud.rankingButtonTextBox.visible = false;
    this.stage.hud.removeChild(this.stage.hud.rankingButtonTextBox);
    if(!this.mute) {
      sound.play('drzwi');
    }
    _delay(() => {
      this.stage.mainScreen.visible = false;
      if (!this.mute & isNull(this.musicId)) {
        this.musicId = sound.play('music');
      }
      this.startLevel();
    }, 2000);
    
  }

  handleRanking() {

    
    this.stage.hud.playButton = " ";
    this.stage.hud.playButtonTextBox.visible = false;
    this.stage.hud.removeChild(this.stage.hud.playButtonTextBox);
    this.stage.hud.rankingButton = " ";
    this.stage.hud.rankingButtonTextBox.visible = false;
    this.stage.hud.removeChild(this.stage.hud.rankingButtonTextBox);

    //this.stage.visible = false;
    this.stage.addChild(this.highscore);
    this.highscore.visible = true;
    this.highscore.show();
    
  }

  removeRanking() {

    
    this.stage.hud.playButton = " ";
    this.stage.hud.playButtonTextBox.visible = true;
    this.stage.hud.addChild(this.stage.hud.playButtonTextBox);
    this.stage.hud.rankingButton = " ";
    this.stage.hud.rankingButtonTextBox.visible = true;
    this.stage.hud.addChild(this.stage.hud.rankingButtonTextBox);

    this.highscore.visible = false;
    this.highscore.hide();
    this.stage.removeChild(this.highscore);
 
    
  }

  handlePost() {
    if (this.highscore.postSent === false){
    var person = prompt("Podaj nick", "Motorniczy");

    if (person == null || person == "") {
      person = "Motorniczy";
    } else {
      person = person.substring(0,16);
    }
    var test = this.highscore.post(person,this.scoreVal);
    if (test) {
      this.stage.hud.postButton = "Wynik dopisany!";
    }
    } else 
      {
        alert("Wynik już dodany. \nPodziel się ze znajomymi i zagraj jeszcze raz!");
      }
  }

  handleShare() {
    window.open("https://www.facebook.com/sharer/sharer.php?u=http%3A%2F%2Furatujtecze.pl%2F%3Fsc%3D" + this.scoreVal);
  }

  updateScore(tramsShot) {
    this.tramsShot += tramsShot;
    this.tramsShotThisWave += tramsShot;
    this.score += tramsShot * this.level.pointsPerTram;
  }

  bindInteractions() {
    this.stage.mousedown = this.stage.touchstart = this.handleClick.bind(this);
  }

  unbindInteractions() {
    this.stage.mousedown = this.stage.touchstart = _noop;
  }

  animate() {
    this.renderer.render(this.stage);

    if (this.shouldWaveEnd()) {
      this.unbindInteractions();
      this.endWave();
    }

    requestAnimationFrame(this.animate.bind(this));
  }
}

export default Game;



// WEBPACK FOOTER //
// ./src/modules/Game.js